import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { UserGroupCouponRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiTabs } from 'noddi-ui-web';
import { CouponCard, useAuthContext } from 'noddi-web';
import { type ReactNode } from 'react';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { getCommonTranslations, getTabsTranslations } from '../../../utils/commonTranslations';
import { getCouponUsageLabel } from '../../../utils/couponUsageDisplay';
import { buildCouponValidUntilLabel } from '../../../utils/couponWalletDisplay';
import {
  isUsedDiscountCoupon,
  splitDiscountCouponsByAvailability,
  splitWalletCouponsByUsageKind
} from '../../../utils/splitWalletCouponsByUsageKind';
import { NoCoupons } from './NoCoupons';

enum DiscountCouponsTabs {
  ACTIVE = 'active',
  USED = 'used'
}

export const Discounts = () => {
  const { currentUserGroupId } = useAuthContext();
  const { currentBrand } = useCurrentBrand();

  const {
    data: coupons,
    isPending: isCouponsPending,
    error: couponsError
  } = noddiAsync.useGet({
    type: URLKeys.getUserGroupCoupons,
    input: { userGroupId: currentUserGroupId!, brandDomain: currentBrand, includeUsed: true }
  });

  if (isCouponsPending) {
    return <NoddiLoadingScreenWithTrans />;
  }
  if (couponsError) {
    return <ApiErrorPageWithTrans error={couponsError} />;
  }

  const { fixedBenefits } = splitWalletCouponsByUsageKind(coupons.results);
  const { active: activeDiscountCoupons, used: usedDiscountCoupons } = splitDiscountCouponsByAvailability(
    coupons.results
  );
  const hasCoupons = coupons.results.length > 0;

  return (
    <ContentWrapperCustomerApp showNewBookingButton title={t`Discounts`}>
      <div className='flex flex-col gap-6'>
        {hasCoupons ? (
          <div className='flex flex-col gap-6'>
            {fixedBenefits.length > 0 && (
              <WalletCouponSection title={<Trans>Fixed benefits</Trans>} coupons={fixedBenefits} />
            )}
            {(activeDiscountCoupons.length > 0 || usedDiscountCoupons.length > 0) && (
              <DiscountCouponsSection activeCoupons={activeDiscountCoupons} usedCoupons={usedDiscountCoupons} />
            )}
          </div>
        ) : (
          <NoCoupons />
        )}
      </div>
    </ContentWrapperCustomerApp>
  );
};

const couponCardTranslations = () => ({
  activated: getCommonTranslations().activated,
  used: getCommonTranslations().used,
  amount: getCommonTranslations().amount,
  discount: getCommonTranslations().discount,
  free: getCommonTranslations().free
});

const UserGroupCouponCard = ({ coupon }: { coupon: UserGroupCouponRecord }) => {
  const translations = couponCardTranslations();
  const usageLabel = isUsedDiscountCoupon(coupon) ? translations.used : getCouponUsageLabel(coupon);
  const campaign = coupon.campaign;

  return (
    <CouponCard
      namePublic={coupon.namePublic}
      descriptionPublic={coupon.descriptionPublic}
      discountAmount={coupon.discount?.amount}
      currency={coupon.discount?.currency}
      discountPercentage={coupon.discountPercentage ?? undefined}
      campaignPartner={
        campaign != null
          ? {
              name: campaign.name,
              image: campaign.image
            }
          : undefined
      }
      maxUses={coupon.maxUses}
      usageLabel={usageLabel ?? undefined}
      validityHint={buildCouponValidUntilLabel(coupon.expiresAt)}
      validForBrands={coupon.validForBrands.map((brand) => ({
        id: brand.id,
        name: brand.name,
        logoUrl: brand.logo?.url
      }))}
      validAtLabel={t`Valid at`}
      translations={translations}
      className='h-full'
    />
  );
};

type DiscountCouponsSectionProps = {
  activeCoupons: UserGroupCouponRecord[];
  usedCoupons: UserGroupCouponRecord[];
};

const DiscountCouponGrid = ({ coupons }: { coupons: UserGroupCouponRecord[] }) => (
  <div className='grid grid-cols-1 gap-3 md:grid-cols-2 md:items-stretch'>
    {coupons.map((coupon) => (
      <UserGroupCouponCard key={coupon.id} coupon={coupon} />
    ))}
  </div>
);

const DiscountCouponsSection = ({ activeCoupons, usedCoupons }: DiscountCouponsSectionProps) => (
  <section className='flex flex-col gap-3'>
    <h2 className='text-lg font-semibold text-primary'>
      <Trans>Discount coupons</Trans>
    </h2>
    <NoddiTabs<DiscountCouponsTabs>
      translations={getTabsTranslations()}
      defaultValue={DiscountCouponsTabs.ACTIVE}
      searchParamName='coupon-tab'
      tabItems={[
        {
          label: t`Active`,
          value: DiscountCouponsTabs.ACTIVE,
          count: activeCoupons.length,
          node:
            activeCoupons.length > 0 ? (
              <DiscountCouponGrid coupons={activeCoupons} />
            ) : (
              <p className='text-sm text-muted-foreground'>
                <Trans>You don't have any active discount coupons.</Trans>
              </p>
            )
        },
        {
          label: t`Expired`,
          value: DiscountCouponsTabs.USED,
          count: usedCoupons.length,
          node:
            usedCoupons.length > 0 ? (
              <DiscountCouponGrid coupons={usedCoupons} />
            ) : (
              <p className='text-sm text-muted-foreground'>
                <Trans>You don't have any expired discount coupons.</Trans>
              </p>
            )
        }
      ]}
    />
  </section>
);

const WalletCouponSection = ({ title, coupons }: { title: ReactNode; coupons: UserGroupCouponRecord[] }) => (
  <section className='flex flex-col gap-3'>
    <h2 className='text-lg font-semibold text-primary'>{title}</h2>
    <div className='grid grid-cols-1 gap-3 md:grid-cols-2 md:items-stretch'>
      {coupons.map((coupon) => (
        <UserGroupCouponCard key={coupon.id} coupon={coupon} />
      ))}
    </div>
  </section>
);
